// Functions to deal with order data payload

import type { Address } from '@/types';
import { SteamShipLinesMap } from '@/utils/constants';

export type BusinessLocation = {
    id: string | number;
    name?: string;
    shortName?: string;
    displayName?: string;
    deliveryAddress?: Address;
    billingAddress?: Address;
};

export type NewOrderFreightContent = {
    quantity?: any;
    packingType?: any;
    isHazmat?: boolean;
    weight?: any;
    description?: any;
    nmfcNumber?: any;
    nmfcClass?: any;
};

export type NewOrderContainer = {
    id?: string | number;
    containerId?: string;
    sizeType?: string;
    sealNumber?: string;
    pickupNumber?: string;
    releaseNumber?: string;
    loadType?: string;
    isAdded?: boolean;

    // Draft fields used by the UI before mutation transform
    size?: any;
    type?: any;

    freightContents: NewOrderFreightContent[];
    __typename?: string;
};

export type NewOrderData = {
    containers: NewOrderContainer[];
    isDropAndHook: boolean;
    orderType: string;

    billOfLadingNumber: string;
    bookingNumber: string;
    pickUpLocation: BusinessLocation;
    dropOffLocation: BusinessLocation;
    billTo: BusinessLocation;

    // reference
    customerReference: string;
    billingCustomerReference: string;
    customerReferences?: string[];
    billingCustomerReferences?: string[];
    linkedMasterOrderNumber: string;
    shipLineInfo: any;
    scacCode: any;

    // contacts
    dropOffContact: any;
    billToContact: any;

    emergencyResponsePhoneNumber: string;
    houseBillOfLading: string;
    brokerSailingInfo: any;
    carrierName: any;
};

export function transformContainersForMutation(containers: any[], isImportOrder: boolean): any[] {
    const newContainers = [];
    containers.forEach((container) => {
        const newContainer = container;
        const normalizedContainerId = String(newContainer.containerId || '').trim();
        if (!isImportOrder && !normalizedContainerId) {
            newContainer.containerId = 'EXPUNDEF';
        } else if (normalizedContainerId) {
            newContainer.containerId = normalizedContainerId;
        }

        if (container.type.value) {
            newContainer.type = container.type.value;
        } else {
            newContainer.type = container.type;
        }

        if ('__typename' in newContainer) {
            delete newContainer.__typename;
        }

        if ('isAdded' in newContainer) {
            delete newContainer.isAdded;
        }

        if (newContainer.containerId) {
            newContainers.push(newContainer);
        }
    });

    return newContainers;
}

export function preparePayloadForMutation(orderData: NewOrderData, isImportOrder: boolean): any {
    const payload: any = {};

    const containers = orderData.containers;

    if (orderData.isDropAndHook) {
        payload.isDropHook = true;
    } else {
        payload.isDropHook = false;
    }

    payload.orderType = orderData.orderType;
    if (isImportOrder) {
        payload.billOfLadingNumber = orderData.billOfLadingNumber;
    } else {
        payload.bookingNumber = orderData.bookingNumber;
    }
    payload.location1Id = orderData.pickUpLocation.id;
    payload.location2Id = orderData.dropOffLocation.id;
    payload.billToId = orderData.billTo.id;

    const customerRefs = (orderData.customerReferences || []).map((x) => String(x || '').trim()).filter((x) => x);
    const billingRefs = (orderData.billingCustomerReferences || []).map((x) => String(x || '').trim()).filter((x) => x);
    const customerReference = String(orderData.customerReference || '').trim();
    const billingCustomerReference = String(orderData.billingCustomerReference || '').trim();

    // Do not send empty arrays, otherwise backend treats them as explicit overwrite and clears legacy fields
    payload.customerReferences = customerRefs.length > 0 ? customerRefs : undefined;
    payload.billingCustomerReferences = billingRefs.length > 0 ? billingRefs : undefined;
    payload.customerReference = customerRefs[0] || customerReference || undefined;
    payload.billingCustomerReference = billingRefs[0] || billingCustomerReference || undefined;
    payload.linkedMasterOrderNumber = orderData.linkedMasterOrderNumber;
    payload.shipLineInfo = orderData.shipLineInfo;
    payload.containers = transformContainersForMutation(containers, isImportOrder);
    // payload.carrierName = orderData.carrierName; // TODO: add this back in when carrierName is added to backend model
    payload.scacCode = orderData.scacCode;
    payload.emergencyResponsePhoneNumber =
        orderData.emergencyResponsePhoneNumber;
    payload.houseBillOfLading = orderData.houseBillOfLading;
    payload.brokerSailingInfo = orderData.brokerSailingInfo;
    return payload;
}

export function addTerminalToOrderData(terminalName: string | undefined, terminals: BusinessLocation[], orderData: NewOrderData): void {
    if (!terminalName) {
        return;
    }
    const terminal = terminals.find((x) => x.name.toLowerCase() === terminalName.toLowerCase() || x.shortName.toLowerCase() === terminalName.toLowerCase());

    if (terminal) {
        orderData.pickUpLocation = terminal;
    }
}

export function addOrderDataShipline(bl: string | undefined, orderData: NewOrderData): void {
    if (!bl) return;

    // TODO: STANDARDIZE_SCAC_CODES
    if (bl.includes('HLCU')) {
        orderData.shipLineInfo.line = 'Hapag-Lloyd';
    } else if (bl.includes('ONEY')) {
        orderData.shipLineInfo.line = 'One Line';
    } else if (bl.includes('HDMU')) {
        orderData.shipLineInfo.line = 'Hyundai Merchant';
    } else if (bl.includes('EGLV')) {
        orderData.shipLineInfo.line = 'Evergreen';
    } else if (bl.includes('ZIMU')) {
        orderData.shipLineInfo.line = 'ZIM Intergrated';
    } else if (bl.includes('MEDU')) {
        orderData.shipLineInfo.line = 'MEDU (Mediterranian Shipping)';
    } else if (bl.includes('CMDU')) {
        orderData.shipLineInfo.line = 'CMA CGM';
    } else if (bl.includes('YMLU') || bl.includes('Yang Ming')) {
        orderData.shipLineInfo.line = 'YMLU (Yang Ming)';
    } else if (bl.includes('APL')) {
        orderData.shipLineInfo.line = 'APLU (American President Lines)';
    } else if (bl.includes('OOLU')) {
        orderData.shipLineInfo.line =
            'OOCL (Orient Overseas Container Line)';
    } else if (bl.includes('MAEU')) {
        orderData.shipLineInfo.line = SteamShipLinesMap.MAEU;
    } else if (bl.includes('WHLU')) {
        orderData.shipLineInfo.line = SteamShipLinesMap.WHLU;
    } else if (bl.includes('SUDU')) {
        orderData.shipLineInfo.line = SteamShipLinesMap.SUDU;
    } else if (bl.includes('SAFM')) {
        orderData.shipLineInfo.line = SteamShipLinesMap.SAFM;
    }
}